import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IntegrationType } from '@app/models/common/integration-type.model';
import { ImportIntegration } from '@app/models/import/import-integration.model';
import { PlatformModule } from '@app/platform.module';
import ConnectionStatus from '@app/types/connection-status.type';
import { TranslatableKey } from '@app/types/translatable.type';
import { IntegrationTypeDescriptionDisplayOptions } from '@app/types/translatables/integration-type-description-display.options';
import { TranslateModule } from '@ngx-translate/core';
import { omitBy } from 'lodash-es';
import { ButtonTypes } from '../platform/button/button.component';
import { CardVariant } from '../platform/card/card.component';
import { SVGImageComponent } from '../svg-image/svg-image.component';

export const INTEGRATION_ACTIONS = {
    CONNECT: 'settings.integration-card.connectButton',
};

@Component({
    standalone: true,
    selector: 'app-integration-card',
    templateUrl: './integration-card.component.html',
    imports: [TranslateModule, PlatformModule, CommonModule, SVGImageComponent],
    styleUrls: ['./integration-card.component.scss'],
})
export class IntegrationCardComponent<
    TIntegration extends IntegrationType | ImportIntegration,
    TActions extends typeof INTEGRATION_ACTIONS,
> {
    private static nextId = 0;

    @Input() id = `integrationCard-${(IntegrationCardComponent.nextId += 1)}`;
    @Input() integrationType?: TIntegration;
    @Input() connectionState?: ConnectionStatus = undefined;
    @Input() availableActions?: TActions;
    @Input() shouldUseSVGLogo = false;
    // One of the provided available actions can be used as a button instead of a meatball menu item, if specified
    @Input() primaryAction?: { action: keyof TActions; buttonVariant?: ButtonTypes };
    @Input() styleOptions: Partial<{ cardVariant: CardVariant }> = {};
    @Input() disabled = false;
    @Output() onAction: EventEmitter<keyof TActions> = new EventEmitter<keyof TActions>();

    imageLoaded = false;

    get logoUrl(): string | undefined {
        if (!this.integrationType) {
            return undefined;
        }

        if (this.integrationType.logo && !this.shouldUseSVGLogo) {
            return this.integrationType.logo;
        }

        const fileType = this.shouldUseSVGLogo ? '.svg' : '.png';

        return `${this.integrationType.assetBaseURL}${this.integrationType.name}${fileType}`;
    }

    // Gets the items for the meatball menu, removing the primary action since that will be displayed in a button
    get meatballMenuItems(): { [key: string]: TranslatableKey } {
        return omitBy(this.availableActions, (_, action) => {
            return action === this.primaryAction?.action;
        });
    }

    get integrationTypeDescriptionTranslatable(): TranslatableKey {
        if (this.integrationType?.description) {
            return (
                IntegrationTypeDescriptionDisplayOptions[this.integrationType.description] ??
                this.integrationType?.description
            );
        }

        return '';
    }
}
